import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { OFFICIAL_IMAGE_CATEGORIES, type OfficialImage } from '../data/official-images';
import { formatBytes } from '@/lib/utils';

function extFromSrc(src: string): string {
  const m = src.match(/\.([a-z0-9]+)(\?|$)/i);
  return m ? m[1].toUpperCase() : '—';
}

function ImageCard({ image }: { image: OfficialImage }) {
  const [size, setSize] = useState<number | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(image.src, { method: 'HEAD' })
      .then((res) => {
        if (cancelled) return;
        const len = res.headers.get('content-length');
        if (len) setSize(parseInt(len, 10));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [image.src]);

  return (
    <div className="border rounded-lg overflow-hidden bg-white flex flex-col">
      <div
        className={`flex items-center justify-center p-6 min-h-[160px] ${
          image.background === 'dark' ? 'bg-[#1C143D]' : 'bg-[#F0F0F0]'
        }`}
      >
        <img
          src={image.src}
          alt={image.name}
          className="max-w-full max-h-[120px] object-contain"
          onLoad={(e) => {
            const el = e.currentTarget;
            setDims({ w: el.naturalWidth, h: el.naturalHeight });
          }}
        />
      </div>
      <div className="p-3 border-t flex-1 flex flex-col">
        <div className="text-sm font-medium text-slate-900">{image.name}</div>
        {image.description && (
          <div className="text-xs text-slate-500 mt-0.5">{image.description}</div>
        )}
        <dl className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-[11px] text-slate-600 mt-2">
          <dt className="text-slate-400">Format</dt>
          <dd className="col-span-2">{extFromSrc(image.src)}</dd>
          <dt className="text-slate-400">Size</dt>
          <dd className="col-span-2">{size === null ? '—' : formatBytes(size)}</dd>
          <dt className="text-slate-400">Dimensions</dt>
          <dd className="col-span-2">{dims ? `${dims.w} × ${dims.h} px` : '—'}</dd>
        </dl>
        <a
          href={image.src}
          download
          className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800"
        >
          <Download className="h-3 w-3" />
          Download
        </a>
      </div>
    </div>
  );
}

export function ImagesTab() {
  return (
    <div className="space-y-8">
      {OFFICIAL_IMAGE_CATEGORIES.map((cat) => (
        <section key={cat.id}>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{cat.title}</h3>
            <span className="text-xs text-slate-400">{cat.images.length} item{cat.images.length === 1 ? '' : 's'}</span>
          </div>
          {cat.description && (
            <p className="text-xs text-slate-500 mb-3">{cat.description}</p>
          )}
          {cat.images.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center text-xs text-slate-500 bg-slate-50">
              No {cat.title.toLowerCase()} uploaded yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {cat.images.map((img) => (
                <ImageCard key={img.src} image={img} />
              ))}
            </div>
          )}
        </section>
      ))}
      <p className="text-xs text-slate-500 pt-2">
        Source: <code className="px-1 rounded bg-slate-100">apps/frontend-react/public/atek/branding/</code>.
        Add a new category by dropping a folder and registering it in{' '}
        <code className="px-1 rounded bg-slate-100">src/features/brand/data/official-images.ts</code>.
      </p>
    </div>
  );
}
